import React, { useState, useEffect, useMemo } from 'react';
import { 
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell, 
    Paper, 
    CircularProgress, 
    TablePagination,
    IconButton,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    InputAdornment
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { catalogApi } from '../../../api/catalogApi.js';
import { AppLayout } from '../../app/appLayout.js';
import { Modal } from '../../shared';
import { ItemFormModal } from './itemFormModal.js';

const ViewItemsScreen = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [editingItem, setEditingItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Filter items based on search query (brand or name)
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return items;
        }
        const query = searchQuery.toLowerCase().trim();
        return items.filter(item => {
            const brand = (item.brand || '').toLowerCase();
            const name = (item.name || '').toLowerCase();
            return brand.includes(query) || name.includes(query);
        });
    }, [items, searchQuery]);

    useEffect(() => {
        const fetchItems = async () => {
            try {
                const data = await catalogApi.getItems();
                setItems(data.data.rows);
            } catch (error) {
                console.error('Error fetching items:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchItems();
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setPage(0); // Reset to first page when search changes
    };

    const handleEdit = (item) => {
        setEditingItem(item);
    };

    const handleCloseEdit = () => {
        setEditingItem(null);
    };

    const handleEditSubmit = async () => {
        // Refresh the items list after edit
        try {
            const data = await catalogApi.getItems();
            setItems(data.data.rows);
        } catch (error) {
            console.error('Error refreshing items:', error);
        }
    };

    const paginatedItems = filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderMobileCard = (item) => (
        <Card key={item.id} sx={{ mb: 2, p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.brand}</Typography>
                </Box>
                <IconButton 
                    size="small" 
                    onClick={() => handleEdit(item)}
                    color="primary"
                >
                    <EditIcon />
                </IconButton>
            </Box>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Typography variant="body2"><strong>Serving:</strong> {item.servingSize} {item.servingUnit}</Typography>
                <Typography variant="body2"><strong>Calories:</strong> {item.calories}</Typography>
                <Typography variant="body2"><strong>Fat:</strong> {item.totalFatG}g | <strong>Carbs:</strong> {item.totalCarbG}g | <strong>Protein:</strong> {item.proteinG}g</Typography>
                <Typography variant="body2"><strong>Sodium:</strong> {item.sodiumMg}mg | <strong>Sugar:</strong> {item.totalSugarG}g</Typography>
            </CardContent>
        </Card>
    );

    const renderDesktopTable = () => (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell><b>Brand</b></TableCell>
                        <TableCell><b>Name</b></TableCell>
                        <TableCell><b>Serving Size</b></TableCell>
                        <TableCell><b>Calories</b></TableCell>
                        <TableCell><b>Total Fat (g)</b></TableCell>
                        <TableCell><b>Saturated Fat (g)</b></TableCell>
                        <TableCell><b>Trans Fat (g)</b></TableCell>
                        <TableCell><b>Cholesterol (mg)</b></TableCell>
                        <TableCell><b>Sodium (mg)</b></TableCell>
                        <TableCell><b>Total Carbs (g)</b></TableCell>
                        <TableCell><b>Fiber (g)</b></TableCell>
                        <TableCell><b>Sugar (g)</b></TableCell>
                        <TableCell><b>Protein (g)</b></TableCell>
                        <TableCell><b>Vitamin A</b></TableCell>
                        <TableCell><b>Vitamin C</b></TableCell>
                        <TableCell><b>Vitamin D</b></TableCell>
                        <TableCell><b>Calcium (mg)</b></TableCell>
                        <TableCell><b>Iron (mg)</b></TableCell>
                        <TableCell><b>Potassium (mg)</b></TableCell>
                        <TableCell><b>Caffeine (mg)</b></TableCell>
                        <TableCell><b>Alcohol (g)</b></TableCell>
                        <TableCell><b>Actions</b></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {paginatedItems.length > 0 ? (
                        paginatedItems.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>{item.brand}</TableCell>
                                <TableCell>{item.name}</TableCell>
                                <TableCell>{item.servingSize} {item.servingUnit}</TableCell>
                                <TableCell>{item.calories}</TableCell>
                                <TableCell>{item.totalFatG}</TableCell>
                                <TableCell>{item.saturatedFatG}</TableCell>
                                <TableCell>{item.transFatG}</TableCell>
                                <TableCell>{item.choleterolMg}</TableCell>
                                <TableCell>{item.sodiumMg}</TableCell>
                                <TableCell>{item.totalCarbG}</TableCell>
                                <TableCell>{item.fiberG}</TableCell>
                                <TableCell>{item.totalSugarG}</TableCell>
                                <TableCell>{item.proteinG}</TableCell>
                                <TableCell>{item.vitaminA}</TableCell>
                                <TableCell>{item.vitaminC}</TableCell>
                                <TableCell>{item.vitadminD}</TableCell>
                                <TableCell>{item.calcium}</TableCell>
                                <TableCell>{item.iron}</TableCell>
                                <TableCell>{item.potassium}</TableCell>
                                <TableCell>{item.caffeineMg}</TableCell>
                                <TableCell>{item.alcoholG}</TableCell>
                                <TableCell>
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleEdit(item)}
                                        color="primary"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={22} align="center">No items found</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            <TablePagination
                component="div"
                count={filteredItems.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
        </>
    );

    return (
        <AppLayout>
            <Paper style={{ padding: 20, margin: 20, overflowX: 'auto' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                    <h2 style={{ margin: 0 }}>View Items</h2>
                    {!loading && (
                        <TextField
                            placeholder="Search by brand or name..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ 
                                minWidth: { xs: '100%', sm: '300px' },
                                maxWidth: { xs: '100%', sm: '400px' }
                            }}
                        />
                    )}
                </Box>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        {isMobile ? (
                            <>
                                {paginatedItems.length > 0 ? (
                                    <>
                                        {paginatedItems.map(renderMobileCard)}
                                        <Box display="flex" justifyContent="center" mt={2}>
                                            <TablePagination
                                                component="div"
                                                count={filteredItems.length}
                                                page={page}
                                                onPageChange={handleChangePage}
                                                rowsPerPage={rowsPerPage}
                                                onRowsPerPageChange={handleChangeRowsPerPage}
                                                rowsPerPageOptions={[5, 10, 25]}
                                                labelRowsPerPage="Items:"
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Typography align="center" sx={{ py: 4 }}>No items found</Typography>
                                )}
                            </>
                        ) : (
                            renderDesktopTable()
                        )}
                    </>
                )}
            </Paper>
            {editingItem && (
                <Modal title="Edit Item" onClose={handleCloseEdit}>
                    <ItemFormModal 
                        item={editingItem} 
                        onClose={handleCloseEdit}
                        onSubmit={handleEditSubmit}
                    />
                </Modal>
            )}
        </AppLayout>
    );
};

export { ViewItemsScreen };
